/**
 * Write the note, or change what one already says.
 *
 * ONE DIALOG FOR BOTH, because inserting a footnote and editing one are the same question asked at
 * two moments -- "what does this note say" -- and the second is the commoner of the two. A note
 * that could be created and never corrected would be the only thing in this editor you cannot take
 * back.
 *
 * IT SHOWS THE NUMBER IT WILL CARRY, and the number is not editable. Somebody who could type "7"
 * would be typing a fact about the document's shape, which is exactly the kind of stale copy the
 * heading numbers and the cross-references were built to stop. The number here is the count of the
 * notes ahead of it and it is shown so that the author can see what the reader will.
 *
 * PLAIN TEXT, AND IT SAYS SO. A footnote in a controlled procedure is a sentence of prose -- "the
 * calibration interval is set by SOP-00014" -- and the body of one is stored as an attribute on the
 * marker rather than as content in the reading order. That rules out formatting inside a note, and
 * it is better to be told that in the box than to find out when bold does nothing.
 */
import React from "react";
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from "@mui/material";

import { CHROME_FONT } from "./EditorToolbar";
import { Grey5, Grey17 } from "../../theme/styles";

export default function FootnoteDialog({ open, number, value, onSave, onRemove, onClose }: {
    open: boolean;
    /** What this note will be numbered, counted at the moment the dialog opened. */
    number: number;
    /** What it says now; empty for a note being written for the first time. */
    value: string;
    onSave: (text: string) => void;
    /** Absent when the note is being created -- there is nothing to take away yet. */
    onRemove?: () => void;
    onClose: () => void;
}) {
    const [text, setText] = React.useState(value);
    React.useEffect(() => { if (open) setText(value); }, [open, value]);

    const save = () => { onSave(text.trim()); onClose(); };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontFamily: CHROME_FONT, fontSize: 18, fontWeight: 500,
                               color: Grey17 }}>
                {onRemove ? `Footnote ${number}` : "Footnote"}
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ fontFamily: CHROME_FONT, fontSize: 13, color: Grey5, mb: 3 }}>
                    {onRemove
                        ? "It sits at the foot of whichever page its marker lands on, and is "
                          + "renumbered as the document changes around it."
                        : `It will be note ${number}, and will sit at the foot of whichever page `
                          + "its marker lands on."}
                </Typography>
                <TextField
                    autoFocus fullWidth multiline minRows={3} maxRows={10}
                    placeholder="What the note says"
                    value={text}
                    onChange={(event) => setText(event.target.value)}
                    // ⌘/Ctrl+Enter SAVES. A multiline box swallows Enter, which is right -- a note
                    // can be two sentences -- so the keyboard needs the other way out or the only
                    // way to finish is to reach for the mouse.
                    onKeyDown={(event) => {
                        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                            event.preventDefault(); save();
                        }
                    }}
                    // MUI v9 MOVED IT. `inputProps` is gone; the input's own props live under
                    // `slotProps.htmlInput`, and the old name is a type error rather than a
                    // warning -- which is the good outcome.
                    slotProps={{ htmlInput: { style: { fontFamily: CHROME_FONT, fontSize: 14 } } }}
                />
                <Typography sx={{ fontFamily: CHROME_FONT, fontSize: 12, color: Grey5, mt: 2 }}>
                    Plain text. A note is a sentence, not a passage, so there is no formatting
                    inside one.
                </Typography>
            </DialogContent>
            <DialogActions>
                {onRemove && (
                    <Box sx={{ flexGrow: 1 }}>
                        <Button onClick={() => { onRemove(); onClose(); }}
                                sx={{ fontFamily: CHROME_FONT, color: "#B3261E" }}>
                            Remove
                        </Button>
                    </Box>
                )}
                <Button onClick={onClose} sx={{ fontFamily: CHROME_FONT }}>Cancel</Button>
                <Button variant="contained" disableElevation onClick={save}
                        sx={{ fontFamily: CHROME_FONT, textTransform: "none" }}>
                    {onRemove ? "Save" : "Insert"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
